import { Marker } from 'maplibre-gl';

import { technicianMarker, technicianMarker2 } from './Markers';

class MapLib {
  constructor({ map, zoom, speed, theme }) {
    this.map = map;
    this.unHighlightedMarkerOpacity = 0.5;
    this.highlightedMarkerOpacity = 1;
    this.zoom = zoom;
    this.speed = speed;
    this.theme = theme;
    this.marker = {};
  }

  jumpTo(lat, long) {
    let centerTo = { speed: this.speed, zoom: this.zoom, essential: true };
    centerTo = {
      ...centerTo,
      center: [long, lat],
    };
    this.map.jumpTo(centerTo);
  }

  static coreMetersToMiles(meters) {
    const miles = meters / 1609.344; // 1 mile = 1609.344 meters
    return miles;
  }

  static coreCreateLayer({
    layerId,
    sourceName,
    markerName,
    layoutProps = {},
    paintProps = {},
  }) {
    return {
      id: layerId,
      type: 'symbol',
      source: sourceName,
      layout: {
        'icon-image': markerName,
        ...layoutProps,
      },
      paint: {
        ...paintProps,
      },
    };
  }

  coreShowLayer({ name }) {
    this.map.setLayoutProperty(name, 'visibility', 'visible');
  }

  coreHideLayer({ name }) {
    this.map.setLayoutProperty(name, 'visibility', 'none');
  }

  coreLoadMarkers() {
    const markers = [technicianMarker, technicianMarker2];
    const promises = markers.map(({ name, height, width, svg }) => {
      return new Promise((resolve, reject) => {
        const img = new Image(width, height);
        const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);

        img.onload = () => {
          this.map.addImage(name, img, { pixelRatio: 2 });
          URL.revokeObjectURL(url);
          resolve();
        };

        img.onerror = e => {
          URL.revokeObjectURL(url);
          reject(e);
        };

        img.src = url;
      });
    });
    return Promise.all(promises);
  }

  static coreCreateSource({ features }) {
    return {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features,
      },
    };
  }

  static coreSvgObjectToImageSrc(svgObj) {
    const xmlSerializer = new XMLSerializer();
    const svgString = xmlSerializer.serializeToString(svgObj);
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
  }

  coreAddMarker({ locationLng, locationLat }) {
    let centerTo = { speed: this.speed, zoom: this.zoom, essential: true };

    const markerCreated = new Marker({ color: 'red' })
      .setLngLat([locationLng, locationLat])
      .addTo(this.map);

    const center = [locationLng, locationLat];

    centerTo = {
      ...centerTo,
      center,
    };

    this.map.jumpTo(centerTo);
    return markerCreated;
  }

  coreCreateCircle({ coordinates, radiusMiles, points = 64, name }) {
    const radiusMeters = radiusMiles * 1609.344;
    const coords = [];
    const { locationLng: lng, locationLat: lat } = coordinates;

    for (let i = 0; i < points; i += 1) {
      const angle = (i * 360) / points;
      const bearing = (angle * Math.PI) / 180;

      const dx = radiusMeters * Math.cos(bearing);
      const dy = radiusMeters * Math.sin(bearing);

      const deltaLng = dx / (111320 * Math.cos((lat * Math.PI) / 180));
      const deltaLat = dy / 110540;

      coords.push([lng + deltaLng, lat + deltaLat]);
    }

    coords.push(coords[0]); // close polygon

    const circleGeoJson = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coords],
      },
    };

    this.map.addSource(name, {
      type: 'geojson',
      data: circleGeoJson,
    });

    this.map.addLayer({
      id: 'radius-circle-fill',
      type: 'fill',
      source: name,
      paint: {
        'fill-color': '#1976d2',
        'fill-opacity': 0.2,
      },
    });

    this.map.addLayer({
      id: 'radius-circle-outline',
      type: 'line',
      source: name,
      paint: {
        'line-color': '#1976d2',
        'line-width': 2,
      },
    });
    return circleGeoJson;
  }

  coreRemoveCircle(name) {
    if (!this.map) return;

    // Remove layers if they exist
    if (this.map.getLayer('radius-circle-fill')) {
      this.map.removeLayer('radius-circle-fill');
    }

    if (this.map.getLayer('radius-circle-outline')) {
      this.map.removeLayer('radius-circle-outline');
    }

    // Remove source if it exists
    if (this.map.getSource(name)) {
      this.map.removeSource(name);
    }
  }

  fitMapToCircle({ center, radiusMiles, padding = 20 }) {
    if (!this.map) return;

    const radiusMeters = radiusMiles * 1609.344;
    const [lng, lat] = center; // [longitude, latitude]

    // Approximate degrees for the radius
    const deltaLat = radiusMeters / 110540; // 1 deg latitude ~ 110.54 km
    const deltaLng = radiusMeters / (111320 * Math.cos((lat * Math.PI) / 180));

    const bounds = [
      [lng - deltaLng, lat - deltaLat], // southwest
      [lng + deltaLng, lat + deltaLat], // northeast
    ];

    this.map.fitBounds(bounds, { padding }); // padding in pixels
  }

  static coreGetBoundingBoxMidpoint(geojson) {
    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;

    function processCoords(coords) {
      if (typeof coords[0] === 'number') {
        const [lng, lat] = coords;

        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      } else {
        coords.forEach(processCoords);
      }
    }

    // Handle FeatureCollection, Feature, or Geometry
    if (geojson.type === 'FeatureCollection') {
      geojson.features.forEach(f => {
        if (f.geometry) processCoords(f.geometry.coordinates);
      });
    } else if (geojson.type === 'Feature') {
      processCoords(geojson.geometry.coordinates);
    } else {
      processCoords(geojson.coordinates);
    }

    if (minLng === Infinity) {
      throw new Error('Invalid or empty GeoJSON');
    }

    return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
  }

  addTechnicianLayer({ layerName, sourceName, geoJson, onClick }) {
    this.map.addSource(sourceName, {
      type: 'geojson',
      data: geoJson,
    });

    this.map.addLayer({
      id: layerName,
      type: 'symbol',
      source: sourceName,
      layout: {
        'icon-image': technicianMarker2.name,
        'icon-size': 1,
        'icon-anchor': 'bottom',
        'text-field': ['get', 'zipcode'], // This is how zipcode value is read into marker
        'text-size': 15,
        'text-font': ['Open Sans Bold'],
        'text-offset': [0, -1.8], // moves label into pin head
        'text-anchor': 'center',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#1976d2', // background color
        'text-halo-width': 30, // thickness of background
        'text-halo-blur': 0, // sharp edges
      },
    });

    this.map.on('click', layerName, e => onClick(e));

    this.map.on('mouseenter', layerName, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', layerName, () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  removeLayersAndSource({ layerName, sourceName }) {
    if (this.map.getLayer(layerName)) {
      this.map.removeLayer(layerName);
    }

    if (this.map.getSource(sourceName)) {
      this.map.removeSource(sourceName);
    }
  }

  static technicianPointsToGeoJSON(technicianData = []) {
    return {
      type: 'FeatureCollection',
      features: technicianData.map(t => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [
            Number(t?.location?.longitude),
            Number(t?.location?.latitude),
          ],
        },
        properties: {
          ...t,
        },
      })),
    };
  }

  /** ------------------------------------ end ------------------------------------ */
}

export default MapLib;
